import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
    Save, AlertCircle, Loader2, Dumbbell, Search, Target, X
} from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import NewExerciseSelector from '../common/NewExerciseSelector';
import ImprovedUserExerciseForm from '../Standalone/ImprovedUserExerciseForm';
import WorkoutFormHeader from './common/WorkoutFormHeader';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import usePaginatedExercises from '../../hooks/usePaginatedExercises';
import WorkoutSortableExerciseItem from './common/WorkoutSortableExerciseItem';
import { normalizeWorkoutRepsMode } from '../../constants/workoutRepsMode';
import { buildDefaultSeriesPlan } from '../../constants/workoutSeriesPlan';
export default function CreateWorkoutModal({ client, isOpen, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    
    // State principale
    const [formData, setFormData] = useState({
        nome: '',
        descrizione: ''
    });
    
    // State esercizi
    const [selectedExercises, setSelectedExercises] = useState([]);
    const [expandedExercise, setExpandedExercise] = useState(null);
    
    // State per filtri avanzati (come in archivio esercizi)
    const [filters, setFilters] = useState({
        equipment: '',
        primaryMuscle: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    
    // WEBAPP-021: Usa hook paginato invece di caricamento completo
    const {
        exercises: availableExercises,
        loading: loadingExercises,
        initialLoading,
        hasMore,
        loadMore,
        search: searchExercises,
        refresh: refreshExercises
    } = usePaginatedExercises({
        initialLimit: 50,
        enableInfiniteScroll: true
    });
    
    // Gestisce il cambio filtri da NewExerciseSelector
    const handleFiltersChange = useCallback((newFilters) => {
        const newEquipment = newFilters.equipment || '';
        const newPrimaryMuscle = newFilters.primaryMuscle || '';
        
        setFilters({
            equipment: newEquipment,
            primaryMuscle: newPrimaryMuscle
        });
        
        // Applica i filtri tramite searchExercises solo se c'è almeno un filtro attivo o ricerca
        const hasFilters = newEquipment || newPrimaryMuscle;
        const hasSearch = searchTerm && searchTerm.trim().length >= 2;
        
        if (hasFilters || hasSearch) {
            searchExercises(hasSearch ? searchTerm : '', {
                equipment: newEquipment && newEquipment.trim() !== '' ? newEquipment.trim() : undefined,
                primaryMuscle: newPrimaryMuscle && newPrimaryMuscle.trim() !== '' ? newPrimaryMuscle.trim() : undefined
            });
        } else {
            // WEBAPP-GYM-011: reset filtri senza testo ricerca → ricarica elenco completo
            searchExercises('', {});
        }
    }, [searchExercises, searchTerm]);
    
    // Gestisce la ricerca testuale
    const handleSearchChange = useCallback((term) => {
        setSearchTerm(term);
        
        // Applica ricerca con filtri attivi solo se c'è almeno ricerca (>=2 caratteri) o filtri
        const hasSearch = term && term.trim().length >= 2;
        const hasFilters = filters.equipment || filters.primaryMuscle;
        
        if (hasSearch || hasFilters) {
            searchExercises(hasSearch ? term.trim() : '', {
                equipment: filters.equipment && filters.equipment.trim() !== '' ? filters.equipment.trim() : undefined,
                primaryMuscle: filters.primaryMuscle && filters.primaryMuscle.trim() !== '' ? filters.primaryMuscle.trim() : undefined
            });
        }
    }, [searchExercises, filters]);
    
    // State UI
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [showAddExerciseForm, setShowAddExerciseForm] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [activeTab, setActiveTab] = useState('selection'); // 'selection' | 'selected'
    const [isFullscreen, setIsFullscreen] = useState(false); // WEBAPP-GYM-004: Stato fullscreen
    
    // Drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    useEffect(() => {
        if (isOpen) {
            // WEBAPP-021: L'hook usePaginatedExercises gestisce già il caricamento iniziale
            setFormData({ nome: '', descrizione: '' });
            setSelectedExercises([]);
            setExpandedExercise(null);
            setError(null);
            setMessage('');
            setValidationErrors({});
            setActiveTab('selection');
        }
    }, [isOpen]);

    // Validazione in tempo reale quando cambiano i dati del form
    useEffect(() => {
        const errors = {};
        
        if (!formData.nome.trim()) {
            errors.nome = 'Il nome della scheda è obbligatorio';
            }
            
        if (selectedExercises.length === 0) {
            errors.exercises = 'Aggiungi almeno un esercizio';
        }
        
        setValidationErrors(errors);
    }, [formData.nome, selectedExercises.length]);

    const createClientRowId = () => (
        typeof crypto !== 'undefined' && crypto.randomUUID
            ? `new-${crypto.randomUUID()}`
            : `new-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    );

    const handleSelectExercise = (exercise) => {
        const catalogId = exercise.esercizio_id || exercise.id;
        if (!catalogId) {
            console.error('Esercizio senza ID catalogo:', exercise);
            setError('Errore: esercizio senza ID valido');
            return;
        }

        const newExercise = {
            ...exercise,
            id: createClientRowId(),
            esercizio_id: catalogId,
            serie: exercise.serie_default || (exercise.is_cardio === 1 ? 1 : 3),
            ripetizioni: exercise.ripetizioni_default || 12,
            reps_mode: 'fixed',
            ripetizioni_min: null,
            ripetizioni_max: null,
            peso: exercise.peso_default || 0,
            tempo_recupero: exercise.tempo_recupero || 90,
            notes: { trainer: null, user: null, system: null },
            set_type: 'normal',
            linked_to_previous: false,
            is_isometric_scheda: 0,
            calorie: null,
            bpm: null,
        };

        setSelectedExercises(prev => [...prev, newExercise]);
    };

    const updateExerciseDetail = (id, field, value) => {
        setSelectedExercises(prev =>
            prev.map(e => {
                if (e.id !== id) return e;
                
                // Validazione: se esercizio è cardio, non può essere isometrico nella stessa scheda
                if (field === 'is_isometric_scheda' && value === 1 && e.is_cardio === 1) {
                    setError('Un esercizio cardio non può essere isometrico nella stessa scheda');
                    return e; // Non modificare
                }
                
                // Se set_type è 'normal', linked_to_previous deve essere false
                if (field === 'set_type' && value === 'normal') {
                    return { ...e, [field]: value, linked_to_previous: false, series_plan: null };
                }

                if (field === 'set_type' && value === 'piramidale') {
                    const serie = parseInt(e.serie, 10) || 3;
                    const reps = parseInt(e.ripetizioni, 10) || 12;
                    return {
                        ...e,
                        set_type: value,
                        reps_mode: 'fixed',
                        ripetizioni_min: null,
                        ripetizioni_max: null,
                        series_plan: buildDefaultSeriesPlan(serie, reps, e.peso),
                        linked_to_previous: false,
                    };
                }

                if (field === 'set_type' && value !== 'piramidale') {
                    return { ...e, [field]: value, series_plan: null };
                }
                
                // Se linked_to_previous è true, il primo esercizio non può essere collegato
                if (field === 'linked_to_previous' && value === true) {
                    const exerciseIndex = prev.findIndex(ex => ex.id === id);
                    if (exerciseIndex === 0) {
                        return e; // Non modificare il primo esercizio
                    }
                }
                
                return { ...e, [field]: value };
            })
        );
    };

    const removeExercise = (id) => {
        setSelectedExercises(prev => prev.filter(e => e.id !== id));
        if (expandedExercise === id) {
            setExpandedExercise(null);
        }
    };

    const handleToggleExpanded = (exerciseId) => {
        setExpandedExercise(prev => prev === exerciseId ? null : exerciseId);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over.id) {
            setSelectedExercises(prev => {
                const oldIndex = prev.findIndex(item => item.id === active.id);
                const newIndex = prev.findIndex(item => item.id === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const handleExerciseCreated = async (result) => {
        // result contiene {success: true, message: '...', exercise_id: 293}
        if (result.success && result.exercise_id) {
            try {
                // Recupera l'esercizio completo dal backend usando l'ID specifico
                const api = getAuthenticatedApi();
                const response = await api.get(`/esercizi.php?id=${result.exercise_id}`);
                
                if (response.data && response.data.id) {
                    const newExercise = response.data;
                    
                    // WEBAPP-021: Ricarica lista esercizi per includere il nuovo
                    refreshExercises();
                    
                    // Seleziona automaticamente l'esercizio appena creato
                    handleSelectExercise(newExercise);
                    
                    setMessage('Esercizio creato e aggiunto con successo!');
                    setMessageType('success');
                } else {
                    console.error('Esercizio creato ma non trovato:', result.exercise_id);
                    setMessage('Esercizio creato ma errore nel caricamento');
                    setMessageType('warning');
                }
            } catch (err) {
                console.error('Errore nel caricamento dell\'esercizio creato:', err);
                setMessage('Esercizio creato ma errore nel caricamento');
                setMessageType('warning');
            }
            
            setTimeout(() => {
                setMessage('');
                setMessageType('');
            }, 3000);
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!formData.nome.trim()) {
            errors.nome = 'Il nome della scheda è obbligatorio';
        }
        
        if (selectedExercises.length === 0) {
            errors.exercises = 'Aggiungi almeno un esercizio';
        }
        
        // WEBAPP-GYM-003: Validazione dati numerici degli esercizi
        selectedExercises.forEach((ex, index) => {
            const exerciseErrors = [];
            
            // Valida Serie (deve essere almeno 1, eccetto cardio che può avere 1)
            const serie = parseInt(ex.serie);
            if (!serie || serie < 1) {
                exerciseErrors.push(`Serie non valide`);
            }
            
            const repsMode = normalizeWorkoutRepsMode(ex.reps_mode);
            if (repsMode === 'amrap') {
                // ok
            } else if (repsMode === 'range') {
                const min = parseInt(ex.ripetizioni_min, 10);
                const max = parseInt(ex.ripetizioni_max, 10);
                if (!min || !max || min < 1 || max < 1 || min > max) {
                    exerciseErrors.push('Range ripetizioni non valido (min ≤ max)');
                }
            } else {
                const reps = parseInt(ex.ripetizioni, 10);
                if (!reps || reps < 1) {
                    const label = ex.is_cardio === 1 ? 'Minuti' : (ex.is_isometric_scheda === 1 ? 'Secondi' : 'Ripetizioni');
                    exerciseErrors.push(`${label} non validi`);
                }
            }
            
            // Valida Peso (può essere 0 per esercizi a corpo libero)
            const peso = parseFloat(ex.peso);
            if (isNaN(peso) || peso < 0) {
                exerciseErrors.push(`Peso non valido`);
            }
            
            // Valida Tempo Recupero (deve essere almeno 0)
            const recupero = parseInt(ex.tempo_recupero);
            if (isNaN(recupero) || recupero < 0) {
                exerciseErrors.push(`Tempo recupero non valido`);
            }
            
            if (exerciseErrors.length > 0) {
                errors[`exercise_${index}`] = `Esercizio "${ex.nome}": ${exerciseErrors.join(', ')}`;
            }
        });
        
        setValidationErrors(errors);
        
        // Mostra messaggio di errore se ci sono problemi
        if (Object.keys(errors).length > 0) {
            const errorMessages = Object.values(errors).join('\n');
            setError(errorMessages);
            return false;
        }
        
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        setError(null);

        try {
            const api = getAuthenticatedApi();
            // Debug: controlla che tutti gli esercizi abbiano un ID
            const invalidExercises = selectedExercises.filter(ex => !(ex.esercizio_id ?? ex.id));
            if (invalidExercises.length > 0) {
                console.error('Esercizi senza ID catalogo:', invalidExercises);
                setError('Errore: alcuni esercizi non hanno un ID valido');
                return;
            }

            const schedaData = {
                nome: formData.nome.trim(),
                descrizione: formData.descrizione.trim(),
                user_id: client.id,
                esercizi: selectedExercises.map(ex => {
                    // WEBAPP-GYM-003: Sanitizza e valida i dati numerici
                    const serie = parseInt(ex.serie) || (ex.is_cardio === 1 ? 1 : 3);
                    const repsMode = normalizeWorkoutRepsMode(ex.reps_mode);
                    let ripetizioni = repsMode === 'amrap' ? 0 : (parseInt(ex.ripetizioni, 10) || 12);
                    const peso = parseFloat(ex.peso) || 0;
                    const tempoRecupero = parseInt(ex.tempo_recupero, 10) || 90;
                    
                    if (ex.is_cardio === 1) {
                        ripetizioni = (parseInt(ex.ripetizioni, 10) || 20) * 60;
                    }
                    
                    return {
                        esercizio_id: ex.esercizio_id ?? ex.id,
                        serie: Math.max(1, serie),
                        ripetizioni: Math.max(0, ripetizioni),
                        ripetizioni_min: repsMode === 'range' ? parseInt(ex.ripetizioni_min, 10) : null,
                        ripetizioni_max: repsMode === 'range' ? parseInt(ex.ripetizioni_max, 10) : null,
                        reps_mode: ex.is_cardio === 1 || ex.is_isometric_scheda === 1 || ex.set_type === 'piramidale' ? 'fixed' : repsMode,
                        peso: Math.max(0, peso), // Almeno 0
                        tempo_recupero: Math.max(0, tempoRecupero), // Almeno 0
                        note: ex.notes ? JSON.stringify(ex.notes) : JSON.stringify({ trainer: null, user: null, system: null }),
                        set_type: ex.set_type || 'normal',
                        series_plan: ex.set_type === 'piramidale' ? ex.series_plan : null,
                        linked_to_previous: ex.linked_to_previous || false,
                        is_isometric_scheda: ex.is_isometric_scheda || 0,
                        calorie: ex.calorie || null,
                        bpm: ex.bpm || null
                    };
                })
            };

            console.log('Dati scheda da inviare:', schedaData);

            await api.post('/schede.php', schedaData);
            onSuccess && onSuccess();
            onClose();

        } catch (err) {
            console.error('Errore nella creazione scheda:', err);
            setError(err.response?.data?.error || 'Errore nel salvataggio della scheda');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className={`${isFullscreen ? 'fixed inset-0 z-[9999] bg-[#faf9fc] dark:bg-gray-950' : 'w-full h-full'} flex flex-col overflow-hidden`}>
                <WorkoutFormHeader
                    mode="create"
                    client={client}
                    formData={formData}
                    setFormData={setFormData}
                    validationErrors={validationErrors}
                    isFullscreen={isFullscreen}
                    onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                    onClose={onClose}
                />

                {/* Messaggi */}
                {(message || error) && (
                    <div className="flex-shrink-0 border-b border-[#dad8e5] bg-[#eeebf5] px-6 py-2 dark:border-white/10 dark:bg-gray-900">
                        {message && (
                            <div className={`p-2 rounded-md text-sm ${
                                messageType === 'success' ? 'bg-green-100 text-green-800' : 
                                messageType === 'error' ? 'bg-red-100 text-red-800' : 
                                'bg-blue-100 text-blue-800'
                            }`}>
                                {message}
                            </div>
                        )}
                        {error && (
                            <div className="p-2 bg-red-100 text-red-800 rounded-md text-sm">
                                {error}
                            </div>
                        )}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="shrink-0 border-b border-gray-200 bg-gray-50/80 dark:border-white/10 dark:bg-gray-900/50">
                    <div className="flex">
                        <button type="button" onClick={() => setActiveTab('selection')} className={`flex-1 px-3 py-2 text-xs font-medium sm:text-sm ${activeTab === 'selection' ? 'border-b-2 border-indigo-600 bg-white text-indigo-700 dark:border-indigo-400 dark:bg-gray-900 dark:text-indigo-300' : 'text-gray-600 dark:text-white/65'}`}>
                            <div className="flex items-center justify-center gap-1.5"><Search className="h-3.5 w-3.5" /><span>{t('gym.membersManager.workoutModal.exerciseSelection')}</span><span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] dark:bg-gray-800">{availableExercises.length}</span></div>
                        </button>
                        <button type="button" onClick={() => setActiveTab('selected')} className={`flex-1 px-3 py-2 text-xs font-medium sm:text-sm ${activeTab === 'selected' ? 'border-b-2 border-indigo-600 bg-white text-indigo-700 dark:border-indigo-400 dark:bg-gray-900 dark:text-indigo-300' : 'text-gray-600 dark:text-white/65'}`}>
                            <div className="flex items-center justify-center gap-1.5"><Target className="h-3.5 w-3.5" /><span>{t('gym.membersManager.workoutModal.selectedExercises')}</span><span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200">{selectedExercises.length}</span></div>
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden min-h-0">
                    {activeTab === 'selection' ? (
                        <div className="h-full min-h-0 overflow-hidden">
                            <NewExerciseSelector
                                searchTerm={searchTerm}
                                exercises={availableExercises}
                                selectedExercises={selectedExercises}
                                onSelectExercise={handleSelectExercise}
                                onShowAddExerciseForm={() => setShowAddExerciseForm(true)}
                                loading={loadingExercises || initialLoading}
                                hasMore={hasMore}
                                onLoadMore={loadMore}
                                onFiltersChange={handleFiltersChange}
                                onSearchChange={handleSearchChange}
                                gymExerciseSelector
                                compact
                            />
                        </div>
                    ) : (
                        <div className="h-full min-h-0 overflow-y-auto p-2 dark:bg-gray-900 sm:p-3">
                            {validationErrors.exercises ? (
                                <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20">
                                    <p className="flex items-center text-xs text-red-600 dark:text-red-400">
                                        <AlertCircle className="mr-1.5 h-3.5 w-3.5" />
                                        {validationErrors.exercises}
                                    </p>
                                </div>
                            ) : null}

                            {selectedExercises.length > 0 ? (
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={selectedExercises.map(e => e.id)}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/40">
                                            <div className="divide-y divide-gray-100 dark:divide-white/5">
                                                {selectedExercises.map((exercise, index) => (
                                                    <WorkoutSortableExerciseItem
                                                        key={exercise.id}
                                                        exercise={exercise}
                                                        previousExercise={index > 0 ? selectedExercises[index - 1] : null}
                                                        onUpdate={updateExerciseDetail}
                                                        onRemove={removeExercise}
                                                        onToggleDetails={handleToggleExpanded}
                                                        isExpanded={expandedExercise === exercise.id}
                                                        isFirst={index === 0}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </SortableContext>

                                </DndContext>
                            ) : (
                                <div className="rounded-xl border border-dashed border-gray-200 px-4 py-10 text-center dark:border-white/10">
                                    <Dumbbell size={40} className="mx-auto mb-2 text-gray-300 dark:text-white/30" />
                                    <p className="text-sm font-medium text-gray-700 dark:text-white/85">
                                        {t('gym.membersManager.workoutModal.noExercisesSelected')}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-white/55">
                                        {t('gym.membersManager.workoutModal.goToSelectionTab')}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('selection')}
                                        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
                                    >
                                        <Search className="h-3.5 w-3.5" />
                                        {t('gym.membersManager.workoutModal.selectExercises')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="shrink-0 border-t border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-white/10 dark:bg-gray-900/80 sm:px-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="text-xs text-gray-600 dark:text-white/70">
                            {selectedExercises.length > 0 ? (
                                <span>
                                    {t('gym.membersManager.workoutModal.summary', {
                                        exercises: selectedExercises.length,
                                        sets: selectedExercises.reduce((total, ex) => total + (ex.serie || 0), 0),
                                    })}
                                </span>
                            ) : null}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/15 dark:bg-gray-800 dark:text-white"
                            >
                                {t('gym.membersManager.workoutModal.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={loading || Object.keys(validationErrors).length > 0}
                                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save size={14} />}
                                {t('gym.membersManager.workoutModal.saveWorkout')}
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            {/* Modal Creazione Esercizio */}
            {showAddExerciseForm && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                    <div className="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#dad8e5] bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                        <div className="flex shrink-0 items-center justify-between border-b border-[#dad8e5] bg-[#dad8e5] px-6 py-4 dark:border-white/10 dark:bg-gray-800">
                            <h3 className="text-lg font-semibold text-[#3a3a3a] dark:font-open-sans dark:text-white">
                                Crea Nuovo Esercizio
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowAddExerciseForm(false)}
                                className="rounded-lg p-2 text-[#3a3a3a] transition-colors hover:bg-black/5 dark:text-white/90 dark:hover:bg-white/10"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <div className="max-h-[calc(85vh-80px)] overflow-y-auto bg-[#faf9fc] p-6 dark:bg-gray-900">
                            <ImprovedUserExerciseForm
                                embedded
                                onClose={() => setShowAddExerciseForm(false)}
                                onSuccess={handleExerciseCreated}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
